import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassLoadingSpinner - Executive design system loading indicator
 *
 * A premium loading spinner with glass morphism effects and shimmer animation.
 *
 * @example
 * ```tsx
 * <GlassLoadingSpinner size="lg" variant="gradient" />
 *
 * <GlassLoadingSpinner.Overlay>
 *   <p>Loading data...</p>
 * </GlassLoadingSpinner.Overlay>
 * ```
 */

export interface GlassLoadingSpinnerProps {
  /** Spinner size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Spinner variant */
  variant?: 'default' | 'gradient' | 'dots';
  /** Label text */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  xs: { spinner: 'w-3 h-3', border: 'border-2', label: 'text-xs' },
  sm: { spinner: 'w-4 h-4', border: 'border-2', label: 'text-sm' },
  md: { spinner: 'w-6 h-6', border: 'border-2', label: 'text-sm' },
  lg: { spinner: 'w-8 h-8', border: 'border-3', label: 'text-base' },
  xl: { spinner: 'w-12 h-12', border: 'border-4', label: 'text-lg' },
};

// Default spinner with ring animation
const RingSpinner: React.FC<{ size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({
  size,
  className,
}) => {
  const sizes = sizeClasses[size];
  return (
    <div
      className={cn(
        sizes.spinner,
        sizes.border,
        'rounded-full animate-spin',
        'border-[var(--accent-blue)]',
        'border-t-transparent',
        className
      )}
    />
  );
};

// Gradient spinner with glow
const GradientSpinner: React.FC<{ size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({
  size,
  className,
}) => {
  const sizes = sizeClasses[size];
  return (
    <div className={cn('relative', sizes.spinner, className)}>
      <div
        className={cn(
          'absolute inset-0 rounded-full animate-spin',
          'bg-gradient-to-r from-blue-500 via-violet-500 to-blue-500'
        )}
        style={{
          mask: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
          padding: size === 'xs' || size === 'sm' ? '2px' : size === 'md' ? '3px' : '4px',
        }}
      />
      <div className="absolute inset-0 rounded-full blur-md opacity-40 bg-gradient-to-r from-blue-500 via-violet-500 to-blue-500 animate-pulse" />
    </div>
  );
};

// Dots loading animation
const DotsSpinner: React.FC<{ size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({
  size,
  className,
}) => {
  const dotSize = {
    xs: 'w-1 h-1',
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
    xl: 'w-3 h-3',
  }[size];

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            dotSize,
            'rounded-full bg-[var(--accent-blue)]',
            'animate-bounce'
          )}
          style={{
            animationDelay: `${i * 0.15}s`,
            animationDuration: '0.6s',
          }}
        />
      ))}
    </div>
  );
};

export const GlassLoadingSpinner: React.FC<GlassLoadingSpinnerProps> & {
  Overlay: typeof LoadingOverlay;
  Skeleton: typeof LoadingSkeleton;
} = ({ size = 'md', variant = 'default', label, className }) => {
  const sizes = sizeClasses[size];

  const SpinnerComponent = {
    default: RingSpinner,
    gradient: GradientSpinner,
    dots: DotsSpinner,
  }[variant];

  return (
    <div className={cn('inline-flex flex-col items-center gap-2', className)}>
      <SpinnerComponent size={size} />
      {label && (
        <span className={cn(sizes.label, 'text-muted-foreground/60')}>{label}</span>
      )}
    </div>
  );
};

/**
 * Full-page loading overlay
 */
interface LoadingOverlayProps {
  /** Loading message */
  message?: string;
  /** Backdrop blur intensity */
  blur?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Children to render below spinner */
  children?: React.ReactNode;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  message,
  blur = true,
  className,
  children,
}) => {
  return (
    <div
      className={cn(
        'fixed inset-0 z-[200] flex flex-col items-center justify-center',
        blur && 'bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm',
        'animate-in fade-in duration-200',
        className
      )}
    >
      <div className="glass-strong rounded-2xl p-8 flex flex-col items-center gap-4 shadow-xl">
        <GradientSpinner size="xl" />
        {message && (
          <p className="text-muted-foreground font-medium">{message}</p>
        )}
        {children}
      </div>
    </div>
  );
};

/**
 * Skeleton loading placeholder
 */
interface LoadingSkeletonProps {
  /** Skeleton variant */
  variant?: 'text' | 'circular' | 'rectangular';
  /** Width (CSS value) */
  width?: string | number;
  /** Height (CSS value) */
  height?: string | number;
  /** Additional CSS classes */
  className?: string;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className,
}) => {
  const baseClass = cn(
    'animate-pulse bg-gradient-to-r',
    'from-slate-200 via-slate-100 to-slate-200',
    'dark:from-slate-700 dark:via-slate-600 dark:to-slate-700',
    'bg-[length:200%_100%]',
    {
      'rounded-md h-4': variant === 'text',
      'rounded-full': variant === 'circular',
      'rounded-lg': variant === 'rectangular',
    },
    className
  );

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (variant === 'circular') {
    style.aspectRatio = '1/1';
  }

  return <div className={baseClass} style={style} />;
};

GlassLoadingSpinner.Overlay = LoadingOverlay;
GlassLoadingSpinner.Skeleton = LoadingSkeleton;
GlassLoadingSpinner.displayName = 'GlassLoadingSpinner';
